/* eslint-disable consistent-return */
import { useMutation } from '@apollo/client';
import { Col, notification, Row } from 'antd';
import { INPUT_TYPE, LABEL_TYPE } from 'components/common/Input/types';
import DAYS, { DayType } from 'constants/days';
import usTimezones from 'constants/usTimezones';
import useFormikRefHook from 'hooks/useFormikRefHook';
import { cloneDeep, flatten, get, isArray, isEmpty } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { CREATE_WORKDAY, UPDATE_WORKDAY } from 'services/graphql/mutations/workdays';
import { CREATE_WORKTIME, DELETE_WORKTIME, UPDATE_WORKTIME } from 'services/graphql/mutations/worktimes';
import { IWorkDay } from 'types/workday';
import { IWorkTime } from 'types/worktime';
import { convertTimeToMinute } from 'utils';
import IntlMessages from 'utils/IntlMessages';

import PortalProfileSaveButton from '../PortalProfileSaveButton';
import { FormActionButtonStyled } from '../styled';
import { IDataForMutations, ITabProps, IWorkTimeInput } from '../types';
import AvailabilityDay from './AvailabilityDay';
import { TimeZonePickerStyled } from './styled';
import { AvailableTimeType } from './types';

const Availability: React.FC<ITabProps> = ({
  user,
  mutateUser,
  refetchUserData,
  hideSaveButton,
  portalButtonClassName,
  submitButtonLabel,
  onSubmit,
  onSubmitCallback,
  showNotificationOnSuccess = true,
  showNotificationOnFailure = true,
}) => {
  const location = useLocation();
  const { pathname } = location;
  const containerClass =
    portalButtonClassName ||
    (pathname.split('/')[1] === 'setup-wizard'
      ? 'steps-content'
      : '.custom-tabs-content-active .gx-tab-content-header');
  const timezonesOptions = useMemo(
    () => usTimezones.US?.map((timezone) => ({ label: timezone.name, value: timezone.value })),
    [],
  );
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const defaultTimeZone = usTimezones.US?.some((tz) => tz.value === userTimezone) ? userTimezone : undefined;
  const clonedUserAvailabilityData = useMemo(() => cloneDeep(user.availability), [user]);
  const [availabilityValues, setAvailabilityValues] = useState<IWorkDay[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedTimezone, setSelectedTimezone] = useState(user.availabilityTimezone || defaultTimeZone);
  const [availableTimeTypes, setAvailableTimetypes] = useState<AvailableTimeType[]>([]);

  useEffect(() => {
    const availableTimeTypesData: AvailableTimeType[] = [];
    const availabilityData = DAYS.map((d: DayType, index) => {
      const existedDayData = clonedUserAvailabilityData
        ? clonedUserAvailabilityData.find((avail) => avail.day.toUpperCase() === d.toUpperCase())
        : undefined;
      if (!existedDayData) {
        availableTimeTypesData[index] = 'anytime';
        return {
          availability: false,
          worktimes: [{ from: '00:00', to: '23:59', id: `new-${Math.random()}` }],
          day: d.toUpperCase(),
        };
      }
      const { availability, worktimes: tempWorktimes, day, id } = existedDayData;
      const worktimes = [...tempWorktimes];
      if (!worktimes.length) {
        worktimes[0] = { from: '00:00', to: '23:59', id: `new-${Math.random()}` };
        availableTimeTypesData[index] = 'anytime';
      } else if (worktimes[0]?.from === '00:00' && worktimes?.[0]?.to === '23:59') {
        availableTimeTypesData[index] = 'anytime';
      } else {
        availableTimeTypesData[index] = 'specific';
      }
      return { availability, worktimes, day, id };
    });
    if (availabilityData.length) setAvailabilityValues(availabilityData);
    if (availableTimeTypesData.length) setAvailableTimetypes(availableTimeTypesData);
  }, [user]);

  const [overlappedStatus, setOverlappedStatus] = useState<Record<string, Record<string, boolean>>[]>([]); // [day[{timeId: {id1: true, id2: false, ...}}]]
  const [invalidTimeRangeError, setInvalidTimeRangeError] = useState<boolean[][]>([]);
  const [invalidTimeValueError, setInvalidTimeValueError] = useState<boolean[][]>([]);

  const [updateWorkday] = useMutation(UPDATE_WORKDAY);
  const [createWorkday] = useMutation(CREATE_WORKDAY);
  const [updateWorktime] = useMutation(UPDATE_WORKTIME);
  const [createWorktime] = useMutation(CREATE_WORKTIME);
  const [deleteWorktime] = useMutation(DELETE_WORKTIME);

  // Mutate workday
  const updateWorkdayFunction = async (value: IDataForMutations) => {
    const updateWorkdayPromise =
      value.updateFilter.length > 0 && value.updatedValue.length > 0
        ? updateWorkday({
            variables: {
              filter: value.updateFilter,
              updatedDoc: value.updatedValue,
            },
          })
        : null;
    const createWorkdayPromise =
      value.newData.length > 0
        ? createWorkday({
            variables: {
              doc: value.newData,
            },
          })
        : null;
    return Promise.all([updateWorkdayPromise, createWorkdayPromise]);
  };

  // Mutate worktime
  const updateWorktimeFunction = async (value: IDataForMutations<IWorkTime>) => {
    const updateWorktimePromise =
      value.updateFilter.length > 0
        ? updateWorktime({
            variables: {
              filter: value.updateFilter,
              updatedDoc: value.updatedValue,
            },
          })
        : null;
    const createWorktimePromise =
      value.newData.length > 0
        ? createWorktime({
            variables: {
              doc: value.newData,
            },
          })
        : null;
    const deleteWorktimePromise =
      value.deleteFilter.length > 0
        ? deleteWorktime({
            variables: {
              ids: value.deleteFilter,
            },
          })
        : null;
    return Promise.all([updateWorktimePromise, createWorktimePromise, deleteWorktimePromise]);
  };

  const updateDataFunction = async (
    workdayData: IDataForMutations<IWorkDay>,
    worktimeData: IDataForMutations<IWorkTime>,
  ) => {
    try {
      await updateWorkdayFunction(workdayData);
      await updateWorktimeFunction(worktimeData);
      if (mutateUser) {
        await mutateUser({
          variables: {
            filter: {
              email: user.email,
            },
            updatedDoc: {
              availabilityTimezone: selectedTimezone,
            },
          },
        });
      }
      if (showNotificationOnSuccess)
        notification.success({
          message: 'Update profile successfully!',
          key: 'updateProfile',
          duration: 2,
        });
      if (refetchUserData) refetchUserData();
    } catch (err) {
      console.error(err);
      if (showNotificationOnFailure)
        notification.error({
          message: 'Update failed! Please try again.',
          key: 'updateProfile',
          duration: 2,
        });
    } finally {
      setLoading(false);
    }
  };

  const checkError = (dayIndex?: number) => {
    if (dayIndex === undefined) {
      const isError = availabilityValues.some((v, index: number) => {
        if (!v.availability) return false;
        const availableTimeTypeCurrentDay = availableTimeTypes[index];
        if (availableTimeTypeCurrentDay === 'anytime') return false;
        if (
          overlappedStatus[index] &&
          Object.values(overlappedStatus[index]).some((errorObject) => Object.values(errorObject).some((e) => e))
        )
          return true;
        if (v && v.availability && (!v.worktimes || v.worktimes.length === 0)) return true;
        if (invalidTimeRangeError[index] && invalidTimeRangeError[index].some((e) => e)) return true;
        if (invalidTimeValueError[index] && invalidTimeValueError[index].some((e) => e)) return true;
        return false;
      });
      return isError;
    }

    // if (overlappedStatus[dayIndex] && overlappedStatus[dayIndex].some((e) => e)) return true;
    if (invalidTimeRangeError[dayIndex] && invalidTimeRangeError[dayIndex].some((e) => e)) return true;
    return false;
  };

  const getWorkdayDataForMutations = (oldData: IWorkDay[], newData: IWorkDay[]) =>
    newData.reduce(
      (acc, newD) => {
        const oldDataOfDay = oldData ? oldData.find((oldD) => oldD.day === newD.day) : undefined;
        if (!oldDataOfDay) {
          acc.newData.push({ ...newD, user: user.id });
        } else {
          acc.updateFilter.push({ id: get(oldDataOfDay, 'id', '') });
          acc.updatedValue.push({ ...newD });
        }
        return acc;
      },
      { updateFilter: [], updatedValue: [], newData: [], deleteFilter: [] } as IDataForMutations,
    );
  const getWorktimeDataForMutations = (data: IWorkTimeInput[]) => {
    const dataForMutation = flatten(data).reduce(
      (acc, val, index) => {
        const availableTimeTypeCurrentDay = availableTimeTypes[index];
        const updatedWorktimeData: Record<string, boolean> = {};
        const oldWorktimesValues = clonedUserAvailabilityData.find((v) => v.id === val.workday)?.worktimes;
        const worktimeArray = val.worktimes;
        const firstWorktime = worktimeArray[0];
        if (
          (!firstWorktime && availableTimeTypeCurrentDay === 'specific') ||
          (worktimeArray.length === 1 &&
            firstWorktime.from === '00:00' &&
            firstWorktime.to === '23:59' &&
            !(firstWorktime?.id || '').includes('new'))
        )
          return acc;
        // eslint-disable-next-line consistent-return
        if (availableTimeTypeCurrentDay === 'specific') {
          worktimeArray.forEach((wt) => {
            const { id: wtId, ...wtValue } = wt;
            if (wt.from === undefined || wt.to === undefined) return acc;
            if (wtId && !wt.id?.includes('new-')) {
              updatedWorktimeData[wtId] = true;
              acc.updateFilter.push({ id: wtId });
              acc.updatedValue.push({ ...wtValue, workday: val.workday });
            } else {
              acc.newData.push({ ...wtValue, workday: val.workday });
            }
          });
        } else {
          acc.newData.push({ from: '00:00', to: '23:59', workday: val.workday });
        }
        if (oldWorktimesValues && oldWorktimesValues.length > 0) {
          oldWorktimesValues.forEach((oldWt) => {
            const oldWtId = oldWt.id;
            if (oldWtId && !updatedWorktimeData[oldWtId]) {
              acc.deleteFilter.push(oldWtId);
            }
          });
        }
        return acc;
      },
      {
        deleteFilter: [],
        newData: [],
        updateFilter: [],
        updatedValue: [],
      } as IDataForMutations<IWorkTime>,
    );
    return dataForMutation;
  };

  // separate workday and worktime values
  const { workday: workdayWithoutWorktime, worktimes } = useMemo(
    () =>
      availabilityValues.reduce(
        (acc, v) => {
          if (!v) return acc;
          const { worktimes: wtime, ...wday } = v;
          if (v.availability) acc.worktimes.push({ worktimes: isArray(wtime) ? [...wtime] : [], workday: v.id });
          acc.workday.push(wday);
          return acc;
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        { workday: [], worktimes: [] } as Record<string, any[]>,
      ),
    [availabilityValues],
  );

  const onSubmitForm = async () => {
    const isError = checkError();
    if (!isError) {
      setLoading(true);
      const { availability } = user;
      const workdayForMutations = getWorkdayDataForMutations(availability, workdayWithoutWorktime);
      const worktimeForMutations = getWorktimeDataForMutations(worktimes);
      if (onSubmit) {
        onSubmit(workdayForMutations, worktimeForMutations);
        return;
      }
      updateDataFunction(workdayForMutations, worktimeForMutations);
      onSubmitCallback?.(workdayForMutations, worktimeForMutations);
    }
  };

  const handleAvailabilitySwitchChange = (v: boolean, index: number) => {
    setAvailabilityValues((c) => {
      const current = [...c];
      if (!current[Number(index)]) current[index] = { availability: v, worktimes: [], day: DAYS[index] };
      else current[Number(index)].availability = v;
      return current;
    });
  };

  const handleAddAvailabilityTime = (dayIndex: number) => {
    setAvailabilityValues((c) => {
      const current = cloneDeep(c);
      if (!current[dayIndex]) current[dayIndex] = { availability: true, worktimes: [], day: DAYS[dayIndex] };
      if (current[dayIndex].worktimes)
        current[dayIndex].worktimes?.push({ from: '', to: '', id: `new-${Math.random()}` });
      else current[dayIndex].worktimes = [{ from: '', to: '', id: `new-${Math.random()}` }];
      return current;
    });
    setInvalidTimeValueError((c) => {
      const current = [...c];
      const newWorktimeValueIndex = availabilityValues[dayIndex].worktimes.length;
      if (!current[dayIndex]) current[dayIndex] = [];
      current[dayIndex][newWorktimeValueIndex] = true;
      return current;
    });
  };

  // check if time is overlapped
  const getOverlappedInputIds = (value: string, dayIndex: number, worktimeIndex: number, type: 'from' | 'to') => {
    const newPickedTime = convertTimeToMinute(value);
    const existedCurrentTime = availabilityValues[dayIndex].worktimes[worktimeIndex][type === 'from' ? 'to' : 'from'];
    const convertedExistedCurrentTime =
      existedCurrentTime !== undefined ? convertTimeToMinute(existedCurrentTime) : undefined;
    if (
      convertedExistedCurrentTime !== undefined &&
      ((type === 'from' && convertedExistedCurrentTime < newPickedTime) ||
        (type === 'to' && convertedExistedCurrentTime > newPickedTime))
    ) {
      return {};
    }
    return availabilityValues[dayIndex].worktimes.reduce((acc, timeData, index) => {
      if (index === worktimeIndex) return acc;
      const timeId = timeData.id || '';
      acc[timeId] = false;
      const timeFrom = timeData.from ? convertTimeToMinute(timeData.from) : 0;
      const timeTo = timeData.to ? convertTimeToMinute(timeData.to) : 0;
      if (newPickedTime > timeFrom && newPickedTime < timeTo) {
        acc[timeId] = true;
      }
      if (convertedExistedCurrentTime === undefined) {
        return acc;
      }
      if (type === 'to') {
        if (newPickedTime > timeTo && convertedExistedCurrentTime < timeTo) {
          acc[timeId] = true;
        }
      } else if (newPickedTime < timeFrom && convertedExistedCurrentTime > timeFrom) {
        acc[timeId] = true;
      }
      return acc;
    }, {} as Record<string, boolean>);
  };

  const handleClearTime = (dayIndex: number, worktimeIndex: number) => {
    setAvailabilityValues((c) => {
      const current = cloneDeep(c);
      if (isArray(get(current, [dayIndex, 'worktimes']))) {
        current[dayIndex].worktimes?.splice(worktimeIndex, 1);
      }
      return current;
    });

    setOverlappedStatus((c) => {
      const current = [...c];
      const currentWorkdayOverlappedStatus = current[dayIndex];
      if (!currentWorkdayOverlappedStatus) return current;
      const currentWorktimeValue = availabilityValues[dayIndex].worktimes[worktimeIndex];
      const currentWorktimeId = currentWorktimeValue.id || '';
      const currentDayOverlappedIds = current[dayIndex][currentWorktimeId];
      if (!currentDayOverlappedIds) return current;
      Object.keys(currentDayOverlappedIds).forEach((id) => {
        delete current[dayIndex][id][currentWorktimeId];
      });
      delete current[dayIndex][currentWorktimeId];
      return current;
    });

    setInvalidTimeRangeError((c) => {
      const current = [...c];
      if (!current[dayIndex]) return current;
      current[dayIndex][worktimeIndex] = false;
      return current;
    });

    setInvalidTimeValueError((c) => {
      const current = [...c];
      if (!current[dayIndex]) return current;
      current[dayIndex].splice(worktimeIndex, 1);
      return current;
    });
  };

  const handleChangeAvailableTimeType = (v: AvailableTimeType, index: number) => {
    setAvailableTimetypes((c) => {
      const current = [...c];
      current[index] = v;
      return current;
    });
  };

  const handlePickTime = (dayIndex: number, value: string, worktimeIndex: number, type: 'from' | 'to') => {
    const currentDayWorktimes = availabilityValues[dayIndex].worktimes;
    const currentWorktimeValue = currentDayWorktimes[worktimeIndex];
    // set time values
    setAvailabilityValues((c) => {
      const current = cloneDeep(c);
      if (!current[dayIndex]) return current;
      current[dayIndex].worktimes[worktimeIndex][type] = value;
      return current;
    });

    // set overlapped status
    setOverlappedStatus((c) => {
      const current = [...c];
      const currentWorktimeId = currentWorktimeValue.id || '';
      if (!current[dayIndex]) {
        current[dayIndex] = {};
      }
      const currentWorkdayOverlappedStatus = current[dayIndex];
      if (!current[dayIndex]) current[dayIndex] = {};
      const overlappedIds = getOverlappedInputIds(value, dayIndex, worktimeIndex, type);
      if (isEmpty(overlappedIds)) return current;
      Object.entries(overlappedIds).forEach(([oId, isOverlapped]) => {
        currentWorkdayOverlappedStatus[currentWorktimeId] = currentWorkdayOverlappedStatus[currentWorktimeId]
          ? { ...currentWorkdayOverlappedStatus[currentWorktimeId], [oId]: isOverlapped }
          : { [oId]: isOverlapped };
        currentWorkdayOverlappedStatus[oId] = currentWorkdayOverlappedStatus[oId]
          ? { ...currentWorkdayOverlappedStatus[oId], [currentWorktimeId]: isOverlapped }
          : { [currentWorktimeId]: isOverlapped };
      });
      return current;
    });

    // set invalid time range err
    const currentExistedWorkTime = convertTimeToMinute(
      type === 'to' ? currentWorktimeValue.from : currentWorktimeValue.to,
    );
    setInvalidTimeRangeError((c) => {
      const current = [...c];
      if (!current[dayIndex]) current[dayIndex] = [];
      const incomingValue = convertTimeToMinute(value);
      if (
        (type === 'from' && incomingValue > currentExistedWorkTime) ||
        (type === 'to' && incomingValue < currentExistedWorkTime)
      ) {
        current[dayIndex][worktimeIndex] = true;
      } else {
        current[dayIndex][worktimeIndex] = false;
      }
      return current;
    });

    // set invalid time err
    setInvalidTimeValueError((c) => {
      const current = [...c];
      if (!current[dayIndex]) current[dayIndex] = [];
      if (!Number.isNaN(currentExistedWorkTime) && !!value) {
        current[dayIndex][worktimeIndex] = false;
      } else {
        current[dayIndex][worktimeIndex] = true;
      }
      return current;
    });
  };

  useFormikRefHook({
    onSubmit: onSubmitForm,
    name: 'availability',
    onValidate: checkError,
  });

  const generateFields = (day: DayType, index: number) => {
    if (!availabilityValues[index]) return null;
    const existedDayData = availabilityValues[index];
    const dayAvailableTimeType = availableTimeTypes[index];
    return (
      <AvailabilityDay
        key={index}
        dayData={existedDayData}
        availableTimeTypeData={dayAvailableTimeType}
        currentDay={day}
        dayIndex={index}
        handleAddAvailabilityTime={handleAddAvailabilityTime}
        handleAvailabilitySwitchChange={handleAvailabilitySwitchChange}
        handleClearTime={handleClearTime}
        handlePickTime={handlePickTime}
        handleChangeAvailableTimeType={handleChangeAvailableTimeType}
        currentDayInvalidTimeRangeError={invalidTimeRangeError[index]}
        currentDayOverlappedStatus={overlappedStatus[index]}
        currentDayInvalidTimeValueError={invalidTimeValueError[index]}
      />
    );
  };

  const onChangeTimezone = (option: any) => {
    setSelectedTimezone(option);
  };

  return (
    <div className='gx-signin-form gx-form-row0 '>
      <TimeZonePickerStyled
        label='Timezone'
        labelType={LABEL_TYPE.left}
        style={{}}
        name='timezone'
        type={INPUT_TYPE.SELECT}
        onChange={onChangeTimezone}
        value={selectedTimezone || defaultTimeZone}
        inputOptions={timezonesOptions}
      />
      <div>{DAYS.map(generateFields)}</div>
      <Row justify='end' className={hideSaveButton ? 'gx-d-none' : ''}>
        <Col span={24}>
          <Row justify='start'>
            <PortalProfileSaveButton containerClass={containerClass}>
              <FormActionButtonStyled
                isLoading={loading}
                buttonType='primary'
                className={`gx-mb-0 gx-mr-0 ${hideSaveButton ? 'gx-d-none' : ''}`}
                onClick={onSubmitForm}>
                {submitButtonLabel || <IntlMessages id='userProfile.saveProfile' />}
              </FormActionButtonStyled>
            </PortalProfileSaveButton>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default React.memo(Availability);
